import { useState } from "react";
import { UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CVUploadZone } from "./CVUploadZone";
import { extractInfoFromCV } from "@/utils/candidate/cv-extractor";
import { toast } from "@/hooks/use-toast";
import { CandidateFormData } from "@/utils/candidate";
import { useTranslation } from "react-i18next";

interface AddCandidateSelectionModalProps {
    isOpen: boolean;
    onClose: () => void;
    onManualCreate: () => void;
    onCVUpload: (formData: CandidateFormData) => void;
}

export const AddCandidateSelectionModal = ({
    isOpen,
    onClose,
    onManualCreate,
    onCVUpload,
}: AddCandidateSelectionModalProps) => {
    const [isUploading, setIsUploading] = useState(false);
    const { t } = useTranslation();

    const handleFileSelect = async (file: File) => {
        setIsUploading(true);
        try {
            toast({
                title: t("candidates.newCandidateSelectionModal.processingCV"),
                description: t("candidates.newCandidateSelectionModal.extractingCV"),
            });
            const extractedInfo = await extractInfoFromCV(file);
            // Sanitize the form data to ensure no DOM references
            const formData: CandidateFormData = {
                firstName: extractedInfo?.firstName || "",
                lastName: extractedInfo?.lastName || "",
                email: extractedInfo?.email || "",
                phone: extractedInfo?.phone || "",
                role: extractedInfo?.role || "",
                skills: extractedInfo?.skills ? extractedInfo.skills.join(", ") : "",
                cvFile: file, // Only include the file reference
                summary: extractedInfo?.summary ? String(extractedInfo.summary) : "",
                linkedin: extractedInfo?.linkedin || "",
                _initialLanguages: extractedInfo?.languages || []
            };
            onCVUpload(formData);
            // Provide more detailed feedback based on what was found
            const extractedFields = Object.keys(extractedInfo).filter(key =>
                extractedInfo[key as keyof typeof extractedInfo] && key !== 'skills'
            ).length;
            const skillsFound = extractedInfo.skills?.length || 0;
            let toastDescription = t("candidates.newCandidateSelectionModal.noCVInfo");
            if (extractedFields > 0 || skillsFound > 0) {
                toastDescription = t("candidates.newCandidateSelectionModal.successExtracted", { fields: extractedFields, skills: skillsFound });
            }
            toast({
                title: t("candidates.newCandidateSelectionModal.cvProcessed"),
                description: toastDescription,
            });
        } catch (error) {
            console.error("Error processing CV:", error);
            toast({
                title: t("candidates.newCandidateSelectionModal.cvError"),
                description: t("candidates.newCandidateSelectionModal.tryAgainOrManual"),
                variant: "destructive",
            });
        } finally {
            setIsUploading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 animate-fade-in">
            <div className="bg-white dark:bg-gray-900 rounded-sm w-full max-w-4xl mx-4 animate-slide-in">
                <div className="flex justify-between items-center p-4 border-b dark:border-gray-700">
                    <h2 className="text-xl font-semibold dark:text-gray-100">{t("candidates.newCandidateSelectionModal.title")}</h2>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                    >
                        <span className="sr-only">{t("common.cancel")}</span>
                        &times;
                    </button>
                </div>
                <div className="p-6">
                    <div className="grid md:grid-cols-2 gap-8">
                        <div className="flex flex-col items-center border dark:border-gray-700 rounded-sm p-6 hover:border-blue-500 transition-colors">
                            <div className="min-h-[300px] w-full mb-4">
                                <CVUploadZone
                                    onFileSelect={handleFileSelect}
                                    isLoading={isUploading}
                                />
                            </div>
                            <h3 className="text-lg font-medium mb-2 dark:text-gray-100">{t("candidates.newCandidateSelectionModal.uploadCV")}</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
                                {t("candidates.newCandidateSelectionModal.uploadCVDesc")}
                            </p>
                        </div>
                        <div
                            className="flex flex-col items-center justify-center border dark:border-gray-700 rounded-sm p-6 hover:border-blue-500 transition-colors cursor-pointer"
                            onClick={onManualCreate}
                        >
                            <div className="bg-gray-100 dark:bg-gray-800 rounded-sm p-6 mb-4">
                                <UserPlus size={64} className="text-gray-500 dark:text-gray-400" />
                            </div>
                            <h3 className="text-lg font-medium mb-2 dark:text-gray-100">{t("candidates.newCandidateSelectionModal.createManual")}</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
                                {t("candidates.newCandidateSelectionModal.createManualDesc")}
                            </p>
                            <Button
                                className="mt-6 w-full"
                                variant="outline"
                                onClick={onManualCreate}
                            >
                                {t("candidates.newCandidateSelectionModal.createManual")}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
